import React, { useEffect, useRef, useState } from 'react';
import { instituteInfo } from '../data/mock';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send, MessageSquare, ArrowUpRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    studentClass: '',
    board: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Please fill in your name and phone number');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || undefined,
        studentClass: formData.studentClass || undefined,
        board: formData.board || undefined,
        message: formData.message || undefined
      });
      toast.success('Thank you! We will contact you shortly.');
      setFormData({ name: '', phone: '', email: '', studentClass: '', board: '', message: '' });
    } catch (err) {
      console.error('Enquiry submission error:', err);
      toast.error('Something went wrong. Please try again or call us directly.');
    }
    setIsSubmitting(false);
  };

  const contactInfo = [
    { icon: MapPin, label: 'Address', value: instituteInfo.address, href: `https://maps.google.com/?q=${encodeURIComponent(instituteInfo.address)}` },
    { icon: Phone, label: 'Phone', value: instituteInfo.phone, href: `tel:${instituteInfo.phone}` },
    { icon: Mail, label: 'Email', value: instituteInfo.email, href: `mailto:${instituteInfo.email}` },
    { icon: Clock, label: 'Hours', value: `Morning: ${instituteInfo.hours.morning} | Evening: ${instituteInfo.hours.evening}`, href: null }
  ];

  return (
    <section id="contact" ref={ref} className="relative py-24 lg:py-32 bg-slate-50">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-200 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-in ${isVisible ? 'is-visible' : ''}`}>
          <Badge className="bg-teal-50 text-teal-700 border-teal-200 px-4 py-1.5 text-sm font-medium mb-6">
            Contact Us
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5">
            Get In{' '}
            <span className="text-teal-600">Touch</span>
          </h2>
          <p className="text-lg text-slate-500">
            Ready to start your child's academic journey? Reach out to us for admissions, enquiries, or any questions.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-12">
          {/* Contact info */}
          <div className={`lg:col-span-2 space-y-5 scroll-fade-in ${isVisible ? 'is-visible' : ''}`}>
            {contactInfo.map((info, index) => {
              const Content = (
                <div className="flex gap-4 p-4 rounded-2xl bg-white border border-slate-100 hover:border-teal-100 hover:shadow-md transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-100 transition-colors duration-300">
                    <info.icon className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-400 mb-1">{info.label}</p>
                    <p className="text-base font-medium text-slate-700 break-words">{info.value}</p>
                  </div>
                  {info.href && <ArrowUpRight className="w-4 h-4 text-slate-300 flex-shrink-0 ml-auto mt-1 group-hover:text-teal-500 transition-colors" />}
                </div>
              );
              return info.href ? (
                <a key={index} href={info.href} target="_blank" rel="noopener noreferrer" className="block">
                  {Content}
                </a>
              ) : (
                <div key={index}>{Content}</div>
              );
            })}

            {/* Google Maps embed */}
            <div className="mt-6 rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
              <iframe
                src={instituteInfo.mapUrl}
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="ENIAC Location"
                className="w-full"
              />
            </div>
          </div>

          {/* Contact form */}
          <Card className={`lg:col-span-3 border-slate-100 shadow-lg shadow-slate-900/5 scroll-fade-in ${isVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '200ms' }}>
            <CardContent className="p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Admission Enquiry</h3>
                  <p className="text-sm text-slate-500">Fill the form and we'll get back to you</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Student/Parent Name *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your name"
                      className="h-12 rounded-xl border-slate-200 focus:border-teal-300 focus:ring-teal-200/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number *</label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 XXXXX XXXXX"
                      className="h-12 rounded-xl border-slate-200 focus:border-teal-300 focus:ring-teal-200/50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email (Optional)</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your.email@example.com"
                    className="h-12 rounded-xl border-slate-200 focus:border-teal-300 focus:ring-teal-200/50"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Class</label>
                    <Input
                      value={formData.studentClass}
                      onChange={(e) => setFormData({ ...formData, studentClass: e.target.value })}
                      placeholder="e.g. Class 8"
                      className="h-12 rounded-xl border-slate-200 focus:border-teal-300 focus:ring-teal-200/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Board</label>
                    <Input
                      value={formData.board}
                      onChange={(e) => setFormData({ ...formData, board: e.target.value })}
                      placeholder="CBSE / ICSE / WBBSE"
                      className="h-12 rounded-xl border-slate-200 focus:border-teal-300 focus:ring-teal-200/50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message (Optional)</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Any specific questions or requirements..."
                    rows={4}
                    className="rounded-xl border-slate-200 focus:border-teal-300 focus:ring-teal-200/50 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-13 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-base shadow-lg shadow-teal-500/15 hover:shadow-teal-500/25 transition-all duration-300 disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Submit Enquiry
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
